import { useEffect, useState } from "react";
import { StyleSheet } from "react-native-unistyles";
import { Input } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";

/** How long typing pauses before the list is asked for, so a number is not read a digit at a time. */
const TYPING_PAUSE_MS = 300;

/** A blank search is no filter, written as no param. */
const clientNoParam = (text: string) => (text.trim() === "" ? undefined : text.trim());

const CLIENT_NUMBER_HINT = "A client number is the asset ID plus BASE01 or BASE02.";

interface ClientNumberFilterProps {
  /** As the URL carries it. */
  readonly clientNo: string | undefined;
  readonly onChange: (clientNo: string | undefined) => void;
  /** What a fleet list narrowed by it shows, e.g. `activations`. Absent on the client list itself. */
  readonly rows?: string;
}

/**
 * The start of a client number, which is the asset id plus Patriot's
 * `BASE01`/`BASE02` suffix — so an asset id finds its clients. Every list
 * that takes one matches it the same way (`FleetClientNoParam`).
 */
export function ClientNumberFilter({ clientNo, onChange, rows }: ClientNumberFilterProps) {
  const helperText =
    rows === undefined ?
      `Finds client numbers starting with this. ${CLIENT_NUMBER_HINT}`
    : `Shows ${rows} of clients whose number starts with this. ${CLIENT_NUMBER_HINT}`;
  const committed = clientNo ?? "";
  const [draft, setDraft] = useState(committed);

  // Follows the URL when it changes underneath — back/forward, or a shared
  // link — but not when it is just this field's own trimmed text coming back.
  useEffect(() => {
    setDraft((current) => (current.trim() === committed ? current : committed));
  }, [committed]);

  useEffect(() => {
    if (draft.trim() === committed.trim()) return;
    const timer = setTimeout(() => onChange(clientNoParam(draft)), TYPING_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [draft, committed, onChange]);

  return (
    <View style={styles.field}>
      <Input
        label="Client"
        floatLabel="always"
        placeholder="Asset ID, e.g. 11238"
        value={draft}
        onChangeText={setDraft}
        clearable
        size="small"
        helperText={helperText}
      />
    </View>
  );
}

/**
 * The clients a fleet list's client filter matched, so a short number's reach
 * is plain. Nothing unfiltered, or when nothing matched — the list says so.
 */
export function MatchedClients({ rows, clientNos }: { readonly rows: string; readonly clientNos?: readonly string[] }) {
  if (clientNos === undefined || clientNos.length === 0) return null;
  return (
    <Text textStyle="xs/regular" color="textMuted">
      {rows} for {clientNos.join(", ")}
    </Text>
  );
}

/** What a fleet list shows when its client filter matched no client. */
export const NO_MATCHED_CLIENTS = "No clients with a number starting with that";

const styles = StyleSheet.create(() => ({
  field: { maxWidth: 420, minWidth: 260 },
}));
